/**
 * DashboardDataScopeSelector — #122 data-scope toggle for parent-org users.
 *
 * Rendered ONLY for users whose company actually has sub-companies (the host
 * gates on a non-empty `subCompanies` list — leaf-company and solo users never
 * see it). Lets a parent-org user narrow the dashboard's company axis between:
 *
 *   - "My company"     → the user's OWN org only (scope `'own'`)
 *   - "All companies"  → the full subtree, own + every descendant (`'all'`)
 *   - <sub-company>     → a single descendant org (`'specific:<id>'`)
 *
 * This is a PURE controlled tab strip: it owns no store subscription and
 * holds no resolution logic — the host derives the company-id set via
 * `resolveScopeCompanyIds` and applies it to the site/report filter chain.
 *
 * Visual language matches the dashboard's existing verdict / company /
 * source-scope chips (uppercase, tracking-widest, Aurora radius, accent
 * `selected-accent` active state) so it sits flush in the same filter row.
 */
import React from 'react';
import {
  type DashboardDataScope,
  specificScope,
} from '../../lib/dashboardDataScope';

export interface DashboardScopeOption {
  /** The descendant company id. */
  id: string;
  /** Display label (disambiguated by `buildCompanyDisplayLabels` upstream). */
  label: string;
}

export interface DashboardDataScopeSelectorProps {
  /** The active scope. Controlled by the host. */
  value: DashboardDataScope;
  onChange: (next: DashboardDataScope) => void;
  /**
   * The descendant sub-companies to offer as individual scope chips. The host
   * passes these only when non-empty (the selector is hidden otherwise), so a
   * leaf-company user never sees the control.
   */
  subCompanies: readonly DashboardScopeOption[];
  className?: string;
}

const baseChip =
  'type-meta px-1.5 py-0.5 uppercase tracking-widest border rounded-md transition-colors';
const activeChip = 'selected-accent';
const idleChip = 'border-border-subtle text-text-muted hover:text-text-secondary';

/**
 * Scope tab strip. Order: My company, All companies, then one chip per
 * descendant sub-company (alpha-sorted upstream).
 */
export function DashboardDataScopeSelector({
  value,
  onChange,
  subCompanies,
  className,
}: DashboardDataScopeSelectorProps): React.ReactElement | null {
  if (subCompanies.length === 0) return null;

  return (
    <div
      className={`flex gap-1 flex-wrap${className ? ` ${className}` : ''}`}
      data-testid="dashboard-data-scope"
      role="group"
      aria-label="Filter dashboard by company scope"
    >
      <button
        type="button"
        data-testid="dashboard-data-scope-own"
        aria-pressed={value === 'own'}
        title="Show only your own company"
        className={`${baseChip} ${value === 'own' ? activeChip : idleChip}`}
        onClick={() => onChange('own')}
      >
        My company
      </button>
      <button
        type="button"
        data-testid="dashboard-data-scope-all"
        aria-pressed={value === 'all'}
        title="Show your company and all sub-companies"
        className={`${baseChip} ${value === 'all' ? activeChip : idleChip}`}
        onClick={() => onChange('all')}
      >
        All companies
      </button>
      {subCompanies.map((company) => {
        const scope = specificScope(company.id);
        const active = value === scope;
        return (
          <button
            key={company.id}
            type="button"
            data-testid={`dashboard-data-scope-${company.id}`}
            aria-pressed={active}
            title={`Show only ${company.label}`}
            className={`${baseChip} ${active ? activeChip : idleChip}`}
            onClick={() => onChange(scope)}
          >
            {company.label}
          </button>
        );
      })}
    </div>
  );
}
